import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import useSites from './useSites';
import { useViewingSites } from './viewingSites';
import { locationModeFor, townOf } from './locationMode';
import { switchToSite, siteColour, samePageAt } from '../../_services/sites.service';
import BottomSheet from '../common/BottomSheet/BottomSheet';
import useWindowSize from '../new-service/useWindowSize';
import { hapticTick } from '../../_utils/haptics';
import './locationPicker.css';
import { isHidden as isReceptionHidden } from '../../_utils/receptionMode';

/**
 * The ONE location control, in the header of every page (beside the
 * notification bell on desktop; in the page header and the menu on phones).
 * Replaces the sidebar switcher and the four separate "Locations" filters.
 *
 * What it does depends on the page (locationMode.js):
 *   switch  - "Working at Hair LAB Norwich": tap a location to work there.
 *   combine - "Showing 3 locations": tick the ones to add up. Each row can
 *             still "Work here".
 *   hidden  - nothing (billing and other whole-business pages).
 * Single-site salons see nothing anywhere.
 *
 * Desktop opens a panel under the pill; phones open the app's slide-up sheet.
 * Long lists: the working location is pinned first, search appears past 7.
 *
 * Slots:
 *   header - beside the notification bell (desktop's pinned corner, and the
 *            shared TopHeader / Bailey / Diary headers on every size).
 *   mobile - inside a section's phone header (Clients, Team, Catalogue,
 *            Settings, Marketing, Finance); renders on phones only, because
 *            on desktop the same page shows the header one by the bell.
 *   drawer - the phone menu, so switching is always one tap away.
 */

const SEARCH_FROM = 8;
const MOBILE_AT = 1100;

const Tick = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);
const Close = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const shortName = (name) => {
  const n = String(name || '').trim();
  return n.length > 22 ? `${n.slice(0, 21)}…` : n;
};

export default function LocationPicker({ slot = 'header', onBeforeOpen }) {
  const { sites, active, activeSalonId, multi, canAddLocation } = useSites();
  const viewing = useViewingSites();
  const routeLoc = useLocation();
  const history = useHistory();
  const { width } = useWindowSize();
  const isMobile = width <= MOBILE_AT;

  const routeMode = locationModeFor(routeLoc.pathname);
  // Combining needs permission to see other locations' figures; without it
  // the same pill just switches.
  const mode = routeMode === 'combine' && !viewing.enabled ? 'switch' : routeMode;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const wrapRef = useRef(null);

  // Desktop panel: click outside or Escape closes it.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMobile]);

  // The pinned header slot needs room on desktop: page actions under the
  // bell move left while the pill is showing.
  // Phones and tablets: the location lives in the side menu, as it does in
  // the desktop sidebar (Ben, 1 Oct 2026). Neither the header pill nor the
  // pages' 'mobile' slots render there; the menu row ('drawer') always does.
  const showing = multi && mode !== 'hidden' && (slot === 'drawer' || (slot === 'header' && !isMobile));
  // Measured, not guessed: a fixed reserve left page actions (Services' Add)
  // floating mid-header. The pinned corner's real width goes in a CSS var.
  useEffect(() => {
    if (!(showing && slot === 'header' && !isMobile)) return undefined;
    document.body.classList.add('ml-picker-on');
    // How far the pinned corner reaches into the header's content row,
    // measured against that row's own right edge (pages differ: some rows
    // stop short of the window), plus a 12px gap. Not the corner's width:
    // that over-reserved and left Add stranded mid-header.
    const corner = wrapRef.current && wrapRef.current.closest('.notif-bell-pinned-wrap');
    let ro = null;
    const onResize = () => put();
    function put() {
      const row = corner && ((corner.parentElement && corner.parentElement.querySelector('.catalogue-top-nav-content')) || document.querySelector('.catalogue-top-nav-content'));
      if (!corner || !row) return;
      const reach = Math.ceil(row.getBoundingClientRect().right - corner.getBoundingClientRect().left) + 12;
      document.body.style.setProperty('--ml-reserve', `${Math.max(56, reach)}px`);
    }
    if (corner) {
      put();
      if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(put); ro.observe(corner); }
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      document.body.classList.remove('ml-picker-on');
      document.body.style.removeProperty('--ml-reserve');
    };
  }, [showing, slot, isMobile]);

  const workingId = String(activeSalonId || (active && active.salonId) || localStorage.getItem('salonId') || '');
  const ordered = useMemo(() => {
    const list = sites || [];
    const here = list.find((s) => String(s.salonId) === workingId);
    return here ? [here, ...list.filter((s) => s !== here)] : list;
  }, [sites, workingId]);

  if (!showing) return null;

  const working = ordered[0];
  const workingName = (working && working.salonName) || 'This location';
  const selected = draft || viewing.salonIds;
  const query = q.trim().toLowerCase();
  const shown = query ? ordered.filter((s) => `${s.salonName} ${townOf(s)}`.toLowerCase().includes(query)) : ordered;
  const n = viewing.salonIds.length;
  const viewSites = ordered.filter((s) => viewing.salonIds.includes(String(s.salonId)));

  const kicker = mode === 'switch' ? 'Working at' : 'Showing';
  const label = mode === 'switch' ? workingName
    : n === 1 ? ((viewSites[0] && viewSites[0].salonName) || workingName)
      : n === ordered.length ? `All ${n} locations`
        : `${n} locations`;
  const dots = mode === 'switch' ? [working].filter(Boolean) : viewSites.slice(0, 3);

  function close() { setOpen(false); setQ(''); setDraft(null); }
  const toggleOpen = () => {
    if (open) { close(); return; }
    if (onBeforeOpen) onBeforeOpen();
    hapticTick();
    setOpen(true);
  };

  const workAt = async (s) => {
    const id = String(s.salonId);
    if (id === workingId) { close(); return; }
    if (busy) return;
    setBusy(id);
    const dest = samePageAt(window.location, workingId, id, localStorage.getItem('userId'));
    try { await switchToSite(id, dest); } catch (e) { setBusy(null); console.error('switch site failed', e); }
  };
  const tick = (s) => {
    const id = String(s.salonId);
    const on = selected.includes(id);
    const next = on ? selected.filter((x) => x !== id) : [...selected, id];
    if (!next.length) return; // never nothing
    hapticTick();
    setDraft(next);
  };
  const apply = () => { viewing.setSalonIds(selected); close(); };
  const draftN = selected.length;
  const applyLabel = draftN === 1 ? 'Show 1 location' : draftN === ordered.length ? `Show all ${draftN} locations` : `Show ${draftN} locations`;

  const title = mode === 'switch' ? 'Which location are you working at?' : 'Which locations do you want to see?';
  const help = mode === 'switch'
    ? 'Tap one. The diary, tills and clients switch to it.'
    : 'Tick as many as you like. The numbers add them together.';

  const body = (
    <>
      {isMobile && <p className="lp-help">{help}</p>}
      {ordered.length >= SEARCH_FROM && (
        <div className="lp-search">
          <label htmlFor={`lp-q-${slot}`} className="lp-sr">Search locations</label>
          <input id={`lp-q-${slot}`} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or town" autoComplete="off" />
        </div>
      )}
      {mode === 'combine' && (
        <div className="lp-quick">
          <button type="button" className="lp-chip" onClick={() => { hapticTick(); setDraft([workingId]); }}>Just {shortName(workingName)}</button>
          <button type="button" className="lp-chip" onClick={() => { hapticTick(); setDraft(ordered.map((s) => String(s.salonId))); }}>All {ordered.length}</button>
        </div>
      )}
      <div className="lp-list" role={mode === 'combine' ? 'group' : 'list'} aria-label="Locations">
        {shown.map((s) => {
          const id = String(s.salonId);
          const isWorking = id === workingId;
          const on = selected.includes(id);
          const sub = busy === id ? 'Switching…' : isWorking ? 'You are working here' : townOf(s);
          return (
            <div className="lp-row-wrap" key={id}>
              <button
                type="button"
                className={`lp-row${mode === 'switch' && isWorking ? ' is-current' : ''}`}
                onClick={() => (mode === 'switch' ? workAt(s) : tick(s))}
                aria-pressed={mode === 'combine' ? on : isWorking}
                disabled={!!busy}
              >
                {mode === 'combine' && <span className={`lp-box${on ? ' is-on' : ''}`}>{on && <Tick size={15} />}</span>}
                <span className="lp-dot" style={{ background: siteColour(s.salonId) }} />
                <span className="lp-name">
                  <b>{s.salonName}</b>
                  {sub && <small>{sub}</small>}
                </span>
                {mode === 'switch' && isWorking && <span className="lp-tick"><Tick /></span>}
              </button>
              {mode === 'combine' && !isWorking && (
                <button type="button" className="lp-work" onClick={() => workAt(s)} disabled={!!busy}>Work here</button>
              )}
            </div>
          );
        })}
        {!shown.length && <p className="lp-empty">No location matches “{q}”.</p>}
      </div>
    </>
  );

  const foot = (
    <div className="lp-foot">
      {mode === 'combine' && <button type="button" className="lp-apply" onClick={apply}>{applyLabel}</button>}
      {canAddLocation && !isReceptionHidden(['locations', 'billing']) && (
        <button type="button" className="lp-add" onClick={() => { close(); history.push('/add-location'); }}>+ Add a location</button>
      )}
    </div>
  );

  // On a phone header: a round badge in the location's colour with its
  // initials ("PS" for PB South), or how many are shown when combining. The
  // sheet it opens does the explaining.
  const initials = (n) => String(n || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  const iconOnly = isMobile && slot === 'mobile';
  const badgeSite = (mode === 'switch' ? working : viewSites[0]) || working;
  const badge = (
    <button
      type="button"
      className={`lp-badge${mode === 'combine' && n > 1 ? ' is-count' : ''}`}
      onClick={toggleOpen}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`${kicker} ${label}. Change location`}
      title={`${kicker} ${label}`}
      style={mode === 'combine' && n > 1 ? undefined : { '--lp-c': siteColour(badgeSite && badgeSite.salonId) }}
    >
      {mode === 'combine' && n > 1 ? n : initials(badgeSite && badgeSite.salonName)}
    </button>
  );

  const pill = iconOnly ? badge : (
    <button
      type="button"
      className={`lp-pill lp-pill--${slot}${isMobile ? ' is-mobile' : ''}`}
      onClick={toggleOpen}
      aria-haspopup="dialog"
      aria-expanded={open}
      title={`${kicker} ${label}`}
    >
      <span className="lp-dots">{dots.map((s) => <span key={String(s.salonId)} style={{ background: siteColour(s.salonId) }} />)}</span>
      {/* One line in a header ("Working at Hair LAB"), level with All Staff
          and the bell; two lines only in the phone menu's roomy row. */}
      <span className={`lp-pill-text${slot === 'drawer' ? '' : ' is-inline'}`}>
        <small>{kicker}</small>
        <b>{label}</b>
      </span>
      {slot === 'drawer' ? <span className="lp-change">Change</span> : <span className="lp-caret"><Chevron /></span>}
    </button>
  );

  return (
    <div className={`lp lp--${slot}`} ref={wrapRef}>
      {pill}
      {open && !isMobile && (
        <div className="lp-panel" role="dialog" aria-label={title}>
          <div className="lp-head">
            <div>
              <span className="lp-kind">{mode === 'switch' ? 'Your location' : 'Locations'}</span>
              <h2>{title}</h2>
              <p>{help}</p>
            </div>
            <button type="button" className="lp-close" onClick={close} aria-label="Close"><Close /></button>
          </div>
          {body}
          {foot}
        </div>
      )}
      {/* Portalled to <body>: the phone menu slides away (a transformed
          ancestor would carry a fixed sheet with it), and headers clip. */}
      {isMobile && ReactDOM.createPortal(
        <BottomSheet open={open} onClose={close} title={title} footer={foot} className="lp-sheet">
          {body}
        </BottomSheet>,
        document.body
      )}
    </div>
  );
}
